import React, { useMemo, useState } from "react";
import { Button, Drawer, TextField } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import * as yup from "yup";
import { Formik, FieldArray, ArrayHelpers } from "formik";

import "./ReachOut.styles.scss";
import { useDealActions, useReachOutManage, IApiServiceError } from "store";
import { useActionHook, useDidUpdate } from "hooks";
import { SimpleHead, ValidationError } from "components";
import { ReachOut as ReachOutSuccess } from "components/modals";
import apiErrorsToFormik from "utils/api-errors-to-formik";
import { ReachOutPopup } from "./ReachOutPopup";

const atLeastOneQuestionRequired = (questions: (string | undefined)[] | undefined) => {
  if (questions) {
    return questions.some((question) => question && question.trim() !== "");
  } else {
    return false;
  }
};

const validationSchema = yup.object().shape({
  email: yup.string().email().required(),
  questions: yup
    .array()
    .of(yup.string())
    .test("at-least-one-question", "At least one question is required", (value) => atLeastOneQuestionRequired(value))
});

type Values = {
  email: string;
  questions: string[];
  email_body: string;
};

export const ReachOut: React.FC = () => {
  const { dealId, dealName, email } = useReachOutManage() || {};
  const { setReachOutManageAction, postReachOutAction } = useDealActions();
  const close = () => setReachOutManageAction(null);

  const { action, error, resetError, data, resetData } = useActionHook(postReachOutAction);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openPopup = () => {
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  useDidUpdate(() => {
    if (!dealId) {
      resetError();
      setIsPopupOpen(false);
    }
  }, [dealId]);

  const initialValues: Values = useMemo(() => {
    return {
      email: email || "",
      email_body: "",
      questions: ["", "", ""]
    };
  }, [email]);

  return (
    <>
      <Drawer
        sx={{
          ".MuiPaper-root": {
            borderTopRightRadius: "25px",
            borderBottomRightRadius: "25px"
          }
        }}
        open={!!dealId}
        onClose={close}
      >
        <Formik
          validationSchema={validationSchema}
          initialValues={initialValues}
          validateOnChange={false}
          initialErrors={error ? apiErrorsToFormik(initialValues, (error as IApiServiceError).errors) : {}}
          validateOnBlur
          onSubmit={({ questions, ...rest }) => {
            if (dealId) {
              action({ dealId, ...rest, questions: questions.filter(Boolean) });
            }
          }}
          enableReinitialize
          validateOnMount={false}
        >
          {({ values, handleChange, handleSubmit, errors, isValid, handleBlur, validateForm }) => (
            <form className="reach-out" onSubmit={handleSubmit}>
              <SimpleHead onIconClick={close}>Request for update - {dealName}</SimpleHead>
              <div className="body">
                <TextField
                  label="Email Address"
                  value={values.email}
                  name="email"
                  onChange={handleChange}
                  helperText={errors.email}
                  error={!!errors.email}
                  onBlur={handleBlur}
                />
                <div className="reach-out__split" />
                <FieldArray name="questions">
                  {({ push }: ArrayHelpers) => (
                    <div className="reach-out__questions-set">
                      {values.questions.map((_, index) => (
                        <TextField
                          key={index}
                          name={"questions." + index}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          multiline
                          placeholder="Insert question"
                          rows={2}
                          maxRows={4}
                        />
                      ))}

                      {errors.questions && <ValidationError messages={errors.questions} />}
                      <div className="reach-out__add" onClick={() => push("")}>
                        <AddCircleOutlineIcon />
                        <span>Question</span>
                      </div>
                    </div>
                  )}
                </FieldArray>
              </div>

              <div className="footer">
                <Button onClick={close} variant="outlined">
                  Cancel
                </Button>
                <Button
                  disabled={!isValid}
                  variant="contained"
                  onClick={async () => {
                    await validateForm();
                    if (isValid) {
                      openPopup();
                    } else {
                      close();
                    }
                  }}
                >
                  Continue
                </Button>
              </div>
              <ReachOutPopup
                isOpen={isPopupOpen}
                onClose={closePopup}
                onSubmit={handleSubmit}
                values={values}
                handleChange={handleChange}
                errors={errors}
                handleBlur={handleBlur}
              />
            </form>
          )}
        </Formik>
      </Drawer>
      <ReachOutSuccess
        data={data}
        close={() => {
          resetData();
          closePopup();
          close();
        }}
      />
    </>
  );
};
